import moment from "moment"
import {
  formatTimestampToTxt
} from '@/utils/common'

/**
 * 判断该数据是否异常(undefined/null/'')
 * @param data 
 */
export function isErrorData(data: any) {
  return data === undefined || data === null || data === ''
}

/**
 * 计算某类能耗在总能耗中的占比，百分比保留一位小数，兜底不能超过100
 * @param current 
 * @param total 
 */
export function calcRatio(current, total) {
  if (isErrorData(current) || isErrorData(total)) {
    return 0
  }
  if (total === 0) {
    return 0
  }
  const formatCurrent = +(Number(current).toFixed(1))
  const formatTotal = +(Number(total).toFixed(1))
  const ratio = +(((formatCurrent / formatTotal) * 100).toFixed(1))
  return ratio <= 100 ? ratio : 100
}

/**
 * 格式化能耗报告数据，保留小数点后一位，无数据展示'--'
 * @param data 
 */
export function formatEnergyData(data: number | string) {
  if (data === null || data === undefined || data === '') {
    return '--'
  }
  return Number(data).toFixed(1)
}

/**
 * 根据人车绑定日期获取最近7天以及最近n个或最近12个自然周选项列表，格式为[{ value: timestamp[], label: string }]
 * 最近7天：从有数据的昨天起算，往前推7天
 * 若人车绑定日期晚于7天前，只生成最近7天数据，不往前推自然周
 * 若人车绑定日期至今不够最近7天+12个自然周的时间，判断人车绑定日期至今有几个自然周，重新获取几个自然周的数据，最远的自然周起始时间为当周周一，自然周某天无数据服务端给null
 * 若人车绑定日期早于12个自然周前，则给最近12个自然周
 * @param {number} validStartTime
 */
export function getWeekOptionsForEnergyInfo(validStartTime: number, validEndTime: number) {
  // 定义变量存储返回的options
  const weekOptions: Array<{ value: number[], label: string }> = []

  // 当天0点时间戳
  const todayTimestamp = new Date().setHours(0,0,0,0)
  let baseTimestamp: number
  if (!validEndTime || validEndTime >= todayTimestamp) {
    baseTimestamp = todayTimestamp
  } else {
    baseTimestamp = new Date(validEndTime).setHours(0,0,0,0) + 24 * 3600 * 1000
  }

  // 当前时间的前7天的日期
  // const recent7DaysBefore = moment(todayTimestamp, 'x').subtract(7, "days")
  const recent7DaysBefore = moment(baseTimestamp, 'x').subtract(7, "days")

  // 获取最近12个自然周的数据
  const latest12WeekData = _generateRecentNatureWeek(12, baseTimestamp)

  const logLatest12WeekData = JSON.parse(JSON.stringify(latest12WeekData)).map(item => {
    item.mondayDate = formatTimestampToTxt(+item.mondayDate)
    item.sundayDate = formatTimestampToTxt(+item.sundayDate)
    return item
  })
  console.log('latest12WeekData', logLatest12WeekData)

  let numOfNatureWeek = 0 // 人车绑定日期至今有几个自然周
  const furthestNatureWeekMonday = latest12WeekData[latest12WeekData.length - 1].mondayDate // 倒数第12个自然周的周一

  // 人车绑定日期晚于7天前
  if (validStartTime > +recent7DaysBefore.format('x')) {
    numOfNatureWeek = 0
  }
  // 人车绑定日期早于7天前，晚于倒数第12个自然周的周一，计算人车绑定日期到上个周日的时间包含几个周次，生成对应几个自然周的数据
  else if (validStartTime > new Date(furthestNatureWeekMonday).getTime()) {
    // const todayTimestamp: number = new Date().setHours(0,0,0,0) // 当天0点时间戳
    // const lastWeekdaySunday: number = +moment(todayTimestamp, 'x').weekday(0).format('x') // 上个星期日时间戳
    const lastWeekdaySunday: number = +moment(baseTimestamp, 'x').weekday(0).format('x') // 上个星期日时间戳
    numOfNatureWeek = Math.ceil((lastWeekdaySunday - validStartTime) / (7 * 24 * 3600 * 1000)) // 取值向上取整
  }
  // 人车绑定日期早于12个自然周前，则给最近12个自然周
  else {
    numOfNatureWeek = 12
  }

  /**
   * 根据numOfNatureWeek判断需要展示几个选项
   */
  // const yesterdayTimestamp = todayTimestamp - 1000 // 获取昨天23：59：59的时间戳
  const yesterdayTimestamp = baseTimestamp - 1000 // 获取前一天23：59：59的时间戳
  const recent7Days = `${recent7DaysBefore.format('MM.DD')}~${formatTimestampToTxt(yesterdayTimestamp, 'MM.DD')}`
  // 至少有最近7天选项
  weekOptions.push({
    value: [+recent7DaysBefore.format('x'), yesterdayTimestamp],
    label: `最近7天（${recent7Days}）`
  })
  if (numOfNatureWeek === 0) {
    // 无须加上自然周
  } else if (numOfNatureWeek === 12) {
    latest12WeekData.forEach((item: any) => {
      weekOptions.push({
        value: [item.mondayDate, item.sundayDate],
        label: `${item.inWhichMonthWeek}（${formatTimestampToTxt(+item.mondayDate, 'MM.DD')}~${formatTimestampToTxt(+item.sundayDate, 'MM.DD')}）`
      })
    })
  } else {
    const latestNWeekData = _generateRecentNatureWeek(numOfNatureWeek, baseTimestamp)
    latestNWeekData.forEach((item: any) => {
      weekOptions.push({
        value: [item.mondayDate, item.sundayDate],
        label: `${item.inWhichMonthWeek}（${formatTimestampToTxt(+item.mondayDate, 'MM.DD')}~${formatTimestampToTxt(+item.sundayDate, 'MM.DD')}）`
      })
    })
  }

  console.log('weekOptions', weekOptions)
  return weekOptions
}

/**
 * 生成最近n个自然周options，格式为[{ value: timestamp[], label: string }]
 * 起始日期使用0点的时间，结束日期使用23:59:59的时间
 */
function _generateRecentNatureWeek(n: number, ts: number) {
  // 获取往前推自然周的基准时间
  // 若当天为星期一，则最近7天与上一个自然周重复，基准时间再往前推一个周
  // 若当天为星期日，由于moment将周日指定为一周的第一天，所以上个自然周会是当周周一至周日，基准时间也需要再往前推一周
  // 否则直接以当天0点为基准时间
  let baseTimestamp: number
  const todayTimestamp = new Date().setHours(0,0,0,0)
  const validTs = ts || todayTimestamp
  if (new Date(validTs).getDay() === 1 || new Date(validTs).getDay() === 0) {
    baseTimestamp = validTs - 7 * 24 * 3600 * 1000
  } else {
    baseTimestamp = validTs
  }

  // 如果语言环境将星期一指定为一周的第一天，则 moment().weekday(0) 将会是星期一。 如果星期日是一周的第一天，则 moment().weekday(0) 将会是星期日
  // 获取上一个自然周
  const lastWeekdayMonday = moment(baseTimestamp, 'x').weekday(-6); // 上个星期一
  const lastWeekdaySunday = moment(baseTimestamp, 'x').weekday(0); // 上个星期日
  // 上一个自然周属于哪个月哪一周（根据当前自然周周日日期在本月是否大于等于4号来判断）
  const lastWeekdayInWhichMonthWeek = _calcInWhichMonthWeek(lastWeekdaySunday.format('x'));

  /**
   * 从倒数第二个自然周开始，根据每个自然周周一时间往回-7递归计算自然周
   * 每个周日的时间戳会是0点，手动加上23:59:59
   */
  let weeksInfo: any[] = []; // 用数组存储所有计算出来的自然周信息，每个自然周用 { mondayDate, sundayDate, inWhichMonthWeek } 来保存
  (function loopGenerate (n, lastMonday) {
    if (n === 0) return
    const monday = moment(lastMonday).weekday(-6);
    const sunday = moment(lastMonday).weekday(0);
    const inWhichMonthWeek = _calcInWhichMonthWeek(sunday.format('x'));
    weeksInfo.push({
      mondayDate: monday.format('x'),
      sundayDate: sunday.format('x'),
      inWhichMonthWeek
    })
    n--;
    loopGenerate(n, monday)
  })(n - 1, lastWeekdayMonday)

  // 将上一个自然周加进数组的第一项
  weeksInfo.unshift({
    mondayDate: lastWeekdayMonday.format('x'),
    sundayDate: lastWeekdaySunday.format('x'),
    inWhichMonthWeek: lastWeekdayInWhichMonthWeek
  })

  // sundayDate手动加上23:59:59
  weeksInfo.forEach(item => {
    item.mondayDate = +item.mondayDate
    item.sundayDate = +item.sundayDate + 24 * 3600 * 1000 - 1000 
  })

  return weeksInfo
}


/**
 * 获取某个周在哪个月的第几周，若该周的周日日期在本月大于等于4号，则该周计入本月周数，否则，计入上一月的最后一周
 * @param {number} sunday 该周周日时间戳
 */
function _calcInWhichMonthWeek(sunday: number | string) {
  sunday = Number(sunday) // 转换为数字类型否则后面new Date会报NaN
  const CHINESE_NUMBER_LIST = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二']

  // 获取该周日的日期
  const date = moment(sunday, 'x').format('YYYY-MM-DD')
  const year = date.split('-')[0]
  const month = date.split('-')[1]
  const day = date.split('-')[2]

  // 获取上个月的最后一天的日期，根据这个日期计算上个月最后一个周是第几周
  // 若该周日的月份是1月，调用Date的setDate(0)方法取不到上一年的12.31，这里手工处理
  let lastDayOfLastMonth: any
  if (Number(month) === 1) {
    lastDayOfLastMonth = `${parseInt(year) - 1}-12-31`
  } else {
    lastDayOfLastMonth = moment(new Date(sunday).setDate(0)).format('YYYY-MM-DD')
  }
  const yearOfLast = lastDayOfLastMonth.split('-')[0]
  const monthOfLast = lastDayOfLastMonth.split('-')[1]
  const dayOfLast = lastDayOfLastMonth.split('-')[2]

  let MonthTxt: number | string = ''
  let WeekTxt: number | string = ''

  if (Number(day) >= 4) {
    MonthTxt = CHINESE_NUMBER_LIST[_getMonthWeek(year, month, day).getMonth - 1]
    WeekTxt = _getMonthWeek(year, month, day).getWeek
  } else {
    MonthTxt = CHINESE_NUMBER_LIST[_getMonthWeek(yearOfLast, monthOfLast, dayOfLast).getMonth - 1]
    WeekTxt = _getMonthWeek(yearOfLast, monthOfLast, dayOfLast).getWeek
  }

  return `${MonthTxt}月第${WeekTxt}周`
}

/**
 * 获取当前日期是当月第几周
 * @param a YYYY
 * @param b MM
 * @param c DD
 * @returns 
 */
function _getMonthWeek (a, b, c) {
  /**
    * a = d = 当前日期
    * b = 6 - w = 当前周的还有几天过完(不算今天)
    * a + b 的和在除以7 就是当天是当前月份的第几周
    */
  let date = new Date(a, parseInt(b) - 1, c),
      w = date.getDay(),
      d = date.getDate();
  if (w == 0) {
    w = 7;
  }
  let config = {
    getMonth: date.getMonth() + 1,
    getYear: date.getFullYear(),
    getWeek: Math.ceil((d + 6 - w) / 7)
  }
  return config;
}